import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { environment } from 'services/environment/Environment';
import { appStore } from 'store';

export const useSignalR = (hubUrl: string) => {
  const { token } = appStore.getState();
  const baseURL = environment().api.apiUrl;

  const baseURLSplit = baseURL.split('/');

  const baseURLWithoutVersion = baseURLSplit.slice(0, 3).join('/');

  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!token?.length) return;

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${baseURLWithoutVersion}/hubs/${hubUrl}`, {
        accessTokenFactory: () => token,
        transport: HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    const startConnection = async () => {
      try {
        await newConnection.start();
        console.log('SignalR Connected to ' + hubUrl);
        setIsConnected(true);
      } catch (err) {
        console.warn('SignalR Connection Error:', err);
        setIsConnected(false);
      }
    };

    startConnection();
    setConnection(newConnection);

    newConnection.onreconnected(() => {
      console.log('SignalR reconnected to ' + hubUrl);
      setIsConnected(true);
    });

    newConnection.onclose(() => {
      console.warn('SignalR disconnected from ' + hubUrl);
      setIsConnected(false);
    });

    return () => {
      newConnection.stop();
    };
  }, [token, hubUrl]);

  const send = async <Type>(eventName: string, payload: Type) => {
    if (!connection || connection.state !== HubConnectionState.Connected) {
      console.warn('SignalR not connected yet, skipping event:', eventName);
      return;
    }

    try {
      console.log('Sending via SignalR:', eventName, payload);
      await connection.invoke(eventName, payload);
    } catch (error) {
      console.warn('SignalR Send Error:', error);
    }
  };

  return { connection, isConnected, send };
};
